"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2,
  Save,
  MapPin,
  Phone,
  Mail,
  Globe,
  Calendar,
  Edit,
  Map,
  Upload,
  School,
  Info,
  Hash,
  X,
  ClipboardPaste,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { API_ENDPOINTS } from "../../../constants/config";
import axiosInstance from "../../../utils/axiosInstance";
import { useToast } from "../../../hooks/use-toast";

export function AboutSchool() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { toast } = useToast();
  const [schoolData, setSchoolData] = useState({
    name: "",
    logo: "",
    splashBackgroundColor: "#0263cc",
    schoolcode: "",
    address: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
    location: {
      latitude: 0,
      longitude: 0,
      searchQuery: "",
    },
    phone: "",
    email: "",
    website: "",
    description: "",
    foundedYear: "",
  });

  const [editData, setEditData] = useState({
    name: "",
    logo: "",
    splashBackgroundColor: "#0263cc",
    schoolcode: "",
    address: {
      street: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
    },
    location: {
      latitude: 0,
      longitude: 0,
      searchQuery: "",
    },
    phone: "",
    email: "",
    website: "",
    description: "",
    foundedYear: "",
  });


  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user") || "{}");

    console.log("Current user:", user);

    if (!token) {
      console.error("Authentication token not found");
      return;
    }

    // Check if user is admin (schooladmin or superadmin)
    if (user.role === "schooladmin" || user.role === "superadmin") {
      setIsAdmin(true);
    }

    fetchSchoolData(token);
  }, []);

  const handleLogoChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEditData((prev) => ({
          ...prev,
          logo: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const fetchSchoolData = async (token) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(
        API_ENDPOINTS.schoolAbout,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === "success") {
        const school = response.data.data.school;

        // Ensure address fields are properly populated from the database
        const address = {
          street: school.address?.street || "",
          city: school.address?.city || "",
          state: school.address?.state || "",
          postalCode: school.address?.postalCode || "",
          country: school.address?.country || "",
        };

        // Ensure location is an object and values are numbers
        const location = {
          latitude: Number(school.location?.latitude || 0),
          longitude: Number(school.location?.longitude || 0),
          searchQuery: school.location?.searchQuery || "",
        };

        const schoolDataObj = {
          name: school.name || "",
          logo: school.logo || "",
          splashBackgroundColor: school.splashBackgroundColor || "#0263cc",
          schoolcode: school.schoolcode || "",
          address: address,
          location: location,
          phone: school.phone || "",
          email: school.email || "",
          website: school.website || "",
          description: school.description || "",
          foundedYear: school.foundedYear || "",
        };

        setSchoolData(schoolDataObj);
        setEditData(schoolDataObj);
      }
    } catch (error) {
      console.error("Error fetching school data:", error);
      alert("Failed to load business information.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes(".")) {
      // Handle nested objects like address.street
      const [parent, child] = name.split(".");
      setEditData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setEditData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isAdmin) {
      alert("You don't have permission to update business information.");
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem("token");

      const response = await axiosInstance.patch(
        API_ENDPOINTS.schoolAbout,
        editData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === "success") {
        alert("Business information updated successfully.");
        setIsEditModalOpen(false);

        // Update local state with returned data
        if (response.data.data.school) {
          const updatedSchool = response.data.data.school;
          const updatedSchoolData = {
            name: updatedSchool.name || "",
            logo: updatedSchool.logo || "",
            splashBackgroundColor: updatedSchool.splashBackgroundColor || "#0263cc",
            schoolcode: updatedSchool.schoolcode || "",
            address: updatedSchool.address || {
              street: "",
              city: "",
              state: "",
              postalCode: "",
              country: "",
            },
            location: updatedSchool.location || {
              latitude: 0,
              longitude: 0,
              searchQuery: "",
            },
            phone: updatedSchool.phone || "",
            email: updatedSchool.email || "",
            website: updatedSchool.website || "",
            description: updatedSchool.description || "",
            foundedYear: updatedSchool.foundedYear || "",
          };
          setSchoolData(updatedSchoolData);
          setEditData(updatedSchoolData);
        }
      }
    } catch (error) {
      console.error("Error updating school data:", error);
      alert(
        error.response?.data?.message || "Failed to update business information."
      );
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = () => {
    setEditData(schoolData);
    setIsEditModalOpen(true);
  };

  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setEditData(schoolData); // Reset to original data
  };


  const saveLocationToBackend = async (locationData) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axiosInstance.patch(
        API_ENDPOINTS.schoolAbout,
        { location: locationData },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status === "success") {
        console.log("Location updated successfully");
      }
    } catch (error) {
      console.error("Error updating location:", error);
      alert("Failed to update location. Please try again.");
    }
  };

  const handlePasteCoordinates = async () => {
    try {
      // Read from clipboard
      const text = await navigator.clipboard.readText();
      
      // Parse coordinates - handle formats like:
      // "28.195005082418575, 76.79877300862891"
      // "28.195005082418575,76.79877300862891"
      const coordPattern = /(-?\d+\.?\d*)[,\s]+(-?\d+\.?\d*)/;
      const match = text.match(coordPattern);
      
      if (match && match.length >= 3) {
        const lat = match[1].trim();
        const lng = match[2].trim();
        
        // Validate coordinates
        const latNum = parseFloat(lat);
        const lngNum = parseFloat(lng);
        
        if (isNaN(latNum) || isNaN(lngNum)) {
          toast({
            title: "Invalid Format",
            description: "Could not parse coordinates. Please copy coordinates in format: lat, lng",
            variant: "destructive"
          });
          return;
        }
        
        if (latNum < -90 || latNum > 90) {
          toast({
            title: "Invalid Latitude",
            description: "Latitude must be between -90 and 90",
            variant: "destructive"
          });
          return;
        }
        
        if (lngNum < -180 || lngNum > 180) {
          toast({
            title: "Invalid Longitude",
            description: "Longitude must be between -180 and 180",
            variant: "destructive"
          });
          return;
        }
        
        // Update location in editData
        const newLocation = {
          latitude: latNum,
          longitude: lngNum,
          searchQuery: editData.location.searchQuery || "",
        };
        
        setEditData((prev) => ({
          ...prev,
          location: newLocation,
        }));
        
        toast({
          title: "Success",
          description: "Coordinates pasted successfully"
        });
      } else {
        toast({
          title: "Invalid Format",
          description: "Could not find coordinates. Please copy coordinates in format: lat, lng (e.g., 28.195, 76.798)",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error pasting coordinates:", error);
      toast({
        title: "Error",
        description: "Failed to read clipboard. Please make sure you have copied the coordinates.",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-[#0263cc] mx-auto mb-4" />
          <p className="text-muted-foreground">Loading business information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 mx-auto">
      <div className="flex items-center justify-between mb-4 bg-white p-3 rounded-md shadow-sm">
        <div className="flex items-center gap-2">
          <School className="h-5 w-5 text-[#0263cc]" />
          <h2 className="text-lg font-semibold text-[#0263cc]">Business Profile</h2>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
          {isAdmin && (
            <Button
              onClick={openEditModal}
              className="bg-[#0263cc] hover:bg-[#0255b3]"
            >
              <Edit className="mr-2 h-4 w-4" />
              Edit Business Info
            </Button>
          )}
        </div>
      </div>

      <Card className="overflow-hidden border-[#0263cc]/10 shadow-sm">
        <div className="bg-[#0263cc] h-32 relative">
              {schoolData.logo && (
                <div className="absolute -bottom-16 left-6 h-32 w-32 rounded-xl overflow-hidden border-4 border-white bg-white shadow-md">
                  <img
                    src={schoolData.logo || "/placeholder.svg"}
                    alt={schoolData.name}
                    className="h-full w-full object-contain"
                  />
                </div>
              )}
            </div>

            <CardContent className={`pt-8 ${schoolData.logo ? "mt-16" : ""}`}>
              <div className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-[#0263cc]">
                    {schoolData.name || "Business Name"}
                  </h2>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {schoolData.schoolcode && (
                      <Badge className="bg-[#0263cc]/10 text-[#0263cc] hover:bg-[#0263cc]/20">
                        <Hash className="h-3 w-3 mr-1" />
                        Business Code: {schoolData.schoolcode}
                      </Badge>
                    )}
                    {schoolData.foundedYear && (
                      <Badge className="bg-[#0263cc]/10 text-[#0263cc] hover:bg-[#0263cc]/20">
                        <Calendar className="h-3 w-3 mr-1" />
                        Est. {schoolData.foundedYear}
                      </Badge>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-[#0263cc] flex items-center">
                      <Info className="h-4 w-4 mr-2" />
                      About
                    </h3>

                    <div className="text-sm text-muted-foreground">
                      {schoolData.description ? (
                        <p>{schoolData.description}</p>
                      ) : (
                        <p className="italic">No description available</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-[#0263cc] flex items-center">
                      <MapPin className="h-4 w-4 mr-2" />
                      Contact & Location
                    </h3>

                    <div className="space-y-3">
                      {(schoolData.address.street ||
                        schoolData.address.city) && (
                        <div className="flex items-start gap-3">
                          <MapPin className="h-5 w-5 text-[#0263cc] shrink-0 mt-0.5" />
                          <div>
                            {schoolData.address.street && (
                              <div>{schoolData.address.street}</div>
                            )}
                            <div>
                              {[
                                schoolData.address.city,
                                schoolData.address.state,
                                schoolData.address.postalCode,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </div>
                            {schoolData.address.country && (
                              <div>{schoolData.address.country}</div>
                            )}
                          </div>
                        </div>
                      )}

                      {schoolData.phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="h-5 w-5 text-[#0263cc]" />
                          <span>{schoolData.phone}</span>
                        </div>
                      )}

                      {schoolData.email && (
                        <div className="flex items-center gap-3">
                          <Mail className="h-5 w-5 text-[#0263cc]" />
                          <a
                            href={`mailto:${schoolData.email}`}
                            className="text-[#0263cc] hover:underline"
                          >
                            {schoolData.email}
                          </a>
                        </div>
                      )}

                      {schoolData.website && (
                        <div className="flex items-center gap-3">
                          <Globe className="h-5 w-5 text-[#0263cc]" />
                          <a
                            href={
                              schoolData.website.startsWith("http")
                                ? schoolData.website
                                : `https://${schoolData.website}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#0263cc] hover:underline"
                          >
                            {schoolData.website}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {schoolData.location.latitude !== 0 && (
                  <div className="mt-6 p-4 rounded-lg bg-[#0263cc]/5 border border-[#0263cc]/20">
                    <div className="flex items-center gap-2 text-sm text-[#0263cc] mb-2">
                      <Map className="h-4 w-4" />
                      <span className="font-medium">Map Location</span>
                    </div>
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                      {schoolData.location.searchQuery && (
                        <div className="text-sm">
                          <span className="font-medium">Location:</span>{" "}
                          {schoolData.location.searchQuery}
                        </div>
                      )}
                      <div className="text-sm">
                        <span className="font-medium">Coordinates:</span>{" "}
                        {schoolData.location.latitude.toFixed(6)},{" "}
                        {schoolData.location.longitude.toFixed(6)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

      {/* Edit Modal */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0263cc] flex items-center">
              <Edit className="mr-2 h-5 w-5" />
              Edit Business Information
            </DialogTitle>
            <DialogDescription>
              Update your business's information. All changes will be saved when you click Save.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="edit-name" className="text-sm font-medium">
                    Business Name
                  </Label>
                  <Input
                    id="edit-name"
                    name="name"
                    value={editData.name}
                    onChange={handleInputChange}
                    className="mt-1.5 border-[#0263cc]/20 focus-visible:ring-[#0263cc]"
                    placeholder="Enter business name"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="edit-foundedYear"
                    className="text-sm font-medium"
                  >
                    Founded Year
                  </Label>
                  <Input
                    id="edit-foundedYear"
                    name="foundedYear"
                    type="number"
                    value={editData.foundedYear}
                    onChange={handleInputChange}
                    className="mt-1.5 border-[#0263cc]/20 focus-visible:ring-[#0263cc]"
                    placeholder="e.g. 1985"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="edit-schoolcode"
                    className="text-sm font-medium flex items-center"
                  >
                    <Hash className="h-4 w-4 mr-1" />
                    Business Code
                  </Label>
                  <Input
                    id="edit-schoolcode"
                    name="schoolcode"
                    value={editData.schoolcode}
                    disabled={true}
                    className="mt-1.5 border-[#0263cc]/20 bg-gray-50"
                    placeholder="e.g. 2025001"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Business code is read-only for data consistency
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <Label className="text-sm font-medium">Business Logo</Label>
                <div className="flex flex-col sm:flex-row items-center gap-4 p-4 border border-dashed rounded-lg border-[#0263cc]/30 bg-[#0263cc]/5">
                  {editData.logo ? (
                    <img
                      src={editData.logo || "/placeholder.svg"}
                      alt="Business logo"
                      className="h-24 w-24 object-contain rounded-lg border bg-white p-2"
                    />
                  ) : (
                    <div className="h-24 w-24 flex items-center justify-center border rounded-lg bg-white text-[#0263cc]">
                      <School className="h-10 w-10 opacity-70" />
                    </div>
                  )}

                  <div className="flex-1">
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoChange}
                      className="hidden"
                      id="edit-logo-upload"
                    />
                    <Label
                      htmlFor="edit-logo-upload"
                      className="cursor-pointer block"
                    >
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full border-[#0263cc]/30 text-[#0263cc] hover:bg-[#0263cc]/10"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById("edit-logo-upload").click();
                        }}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {editData.logo ? "Change Logo" : "Upload Logo"}
                      </Button>
                    </Label>
                    <p className="text-xs text-muted-foreground mt-2">
                      Upload a square image for best results. Recommended
                      size: 512x512px.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4 mt-6">
                <Label className="text-sm font-medium">App splash background colour</Label>
                <p className="text-xs text-muted-foreground">
                  Background colour shown behind the logo when users open the mobile app.
                </p>
                <div className="flex flex-wrap items-center gap-4">
                  <input
                    type="color"
                    value={editData.splashBackgroundColor}
                    onChange={(e) =>
                      setEditData((prev) => ({
                        ...prev,
                        splashBackgroundColor: e.target.value,
                      }))
                    }
                    className="h-10 w-14 cursor-pointer rounded border border-[#0263cc]/30 bg-white p-1"
                  />
                  <Input
                    type="text"
                    value={editData.splashBackgroundColor}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^#[0-9A-Fa-f]{0,6}$/.test(v) || /^[0-9A-Fa-f]{0,6}$/.test(v))
                        setEditData((prev) => ({
                          ...prev,
                          splashBackgroundColor: v.startsWith("#") ? v : v ? `#${v}` : "#0263cc",
                        }));
                    }}
                    className="w-28 font-mono text-sm border-[#0263cc]/20 focus-visible:ring-[#0263cc]"
                    placeholder="#0263cc"
                  />
                </div>
              </div>

            </div>

            <Separator className="my-6" />

            <div>
              <h3 className="text-lg font-medium text-[#0263cc] mb-4">
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="edit-phone" className="text-sm font-medium">
                      Phone Number
                    </Label>
                    <Input
                      id="edit-phone"
                      name="phone"
                      value={editData.phone}
                      onChange={handleInputChange}
                      className="mt-1.5 border-[#0263cc]/20 focus-visible:ring-[#0263cc]"
                      placeholder="e.g. +1 (555) 123-4567"
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label
                      htmlFor="edit-website"
                      className="text-sm font-medium"
                    >
                      Website
                    </Label>
                    <Input
                      id="edit-website"
                      name="website"
                      value={editData.website}
                      onChange={handleInputChange}
                      className="mt-1.5 border-[#0263cc]/20 focus-visible:ring-[#0263cc]"
                      placeholder="https://..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator className="my-6" />

            <div>
              <h3 className="text-lg font-medium text-[#0263cc] mb-4">
                Address
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label
                    htmlFor="edit-address.street"
                    className="text-sm font-medium"
                  >
                    Street Address
                  </Label>
                  <Input
                    id="edit-address.street"
                    name="address.street"
                    value={editData.address.street}
                    onChange={handleInputChange}
                    className="mt-1.5 border-[#0263cc]/20 focus-visible:ring-[#0263cc]"
                    placeholder="e.g. 123 Education Ave"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="edit-address.city"
                    className="text-sm font-medium"
                  >
                    City
                  </Label>
                  <Input
                    id="edit-address.city"
                    name="address.city"
                    value={editData.address.city}
                    onChange={handleInputChange}
                    className="mt-1.5 border-[#0263cc]/20 focus-visible:ring-[#0263cc]"
                    placeholder="e.g. Springfield"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                <div>
                  <Label
                    htmlFor="edit-address.state"
                    className="text-sm font-medium"
                  >
                    State/Province
                  </Label>
                  <Input
                    id="edit-address.state"
                    name="address.state"
                    value={editData.address.state}
                    onChange={handleInputChange}
                    className="mt-1.5 border-[#0263cc]/20 focus-visible:ring-[#0263cc]"
                    placeholder="e.g. California"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="edit-address.postalCode"
                    className="text-sm font-medium"
                  >
                    Postal Code
                  </Label>
                  <Input
                    id="edit-address.postalCode"
                    name="address.postalCode"
                    value={editData.address.postalCode}
                    onChange={handleInputChange}
                    className="mt-1.5 border-[#0263cc]/20 focus-visible:ring-[#0263cc]"
                    placeholder="e.g. 90210"
                  />
                </div>

                <div>
                  <Label
                    htmlFor="edit-address.country"
                    className="text-sm font-medium"
                  >
                    Country
                  </Label>
                  <Input
                    id="edit-address.country"
                    name="address.country"
                    value={editData.address.country}
                    onChange={handleInputChange}
                    className="mt-1.5 border-[#0263cc]/20 focus-visible:ring-[#0263cc]"
                    placeholder="e.g. United States"
                  />
                </div>
              </div>
            </div>

            <Separator className="my-6" />

            <div>
              <h3 className="text-lg font-medium text-[#0263cc] mb-4">
                Map Location
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="edit-location.latitude" className="text-sm font-medium">
                    Latitude
                  </Label>
                  <div className="flex gap-2 items-center mt-1.5">
                    <Input
                      id="edit-location.latitude"
                      name="location.latitude"
                      type="number"
                      step="any"
                      value={editData.location.latitude || ""}
                      onChange={handleInputChange}
                      className="border-[#0263cc]/20 focus-visible:ring-[#0263cc]"
                      placeholder="28.6139"
                    />
                    <Button
                      type="button"
                      size="sm"
                      className="bg-green-500 text-white px-2 py-1"
                      onClick={handlePasteCoordinates}
                      title="Paste coordinates from clipboard (format: lat, lng)"
                    >
                      <ClipboardPaste className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="bg-blue-500 text-white px-2 py-1"
                      onClick={() => {
                        window.open('https://www.google.com/maps', '_blank');
                        toast({
                          title: "Instructions",
                          description: "Google Maps opened in new tab. Search for location, copy coordinates (from URL or right-click), then click the paste button to auto-fill both fields.",
                          duration: 5000
                        });
                      }}
                      title="Open Google Maps to search location and copy coordinates"
                    >
                      <Map className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit-location.longitude" className="text-sm font-medium">
                    Longitude
                  </Label>
                  <Input
                    id="edit-location.longitude"
                    name="location.longitude"
                    type="number"
                    step="any"
                    value={editData.location.longitude || ""}
                    onChange={handleInputChange}
                    className="mt-1.5 border-[#0263cc]/20 focus-visible:ring-[#0263cc]"
                    placeholder="77.2090"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Click "Pick on Map" to open Google Maps, search for location, copy coordinates, then use the paste button to auto-fill both fields.
              </p>
            </div>

            <Separator className="my-6" />

            <div>
              <Label htmlFor="edit-description" className="text-sm font-medium">
                Business Description
              </Label>
              <Textarea
                id="edit-description"
                name="description"
                value={editData.description}
                onChange={handleInputChange}
                rows={6}
                className="mt-1.5 border-[#0263cc]/20 focus-visible:ring-[#0263cc]"
                placeholder="Tell us about your business's history, mission, vision, and values..."
              />
              <p className="text-xs text-muted-foreground mt-2">
                This description will be visible to students, parents, and
                visitors.
              </p>
            </div>
          </form>

          <DialogFooter className="bg-muted/20 border-t px-6 py-4">
            <div className="flex gap-3 w-full justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={closeEditModal}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#0263cc] hover:bg-[#0255b3]"
                onClick={handleSubmit}
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}

export default AboutSchool;
